/**
 * Publish Flow Metrics — Frontend client.
 *
 * Reads the per-publish stage-by-stage measurement artifact emitted by
 * `ise-srp-back`'s `finalizeImportFeedProcessingRun`.
 *
 * Endpoint:
 *   GET /publish-flow-metrics/{jazelId}/{correlationId}
 *
 * Response contract (200):
 *   { jazelId, correlationId, metrics }
 *
 * Response contract (404, optional artifact):
 *   { jazelId, correlationId, metrics: null, missingReason: 'NOT_INSTRUMENTED' }
 *
 * Why a thin client (instead of folding into another service):
 *   - Flow metrics is OPTIONAL. Older publish runs (and any run before this
 *     feature shipped) won't have it. UIs need to render a graceful "not yet
 *     captured" empty state without treating the absence as an error.
 *   - Keeping the contract narrow makes the read safe to call from any
 *     publish-flow view without coupling to other API failure modes.
 */

export interface FlowRuleHit {
  ruleId: string
  label: string | null
  matchedCount: number
}

export interface FlowSegmentCount {
  segmentId: string
  label: string | null
  vehicleCount: number
}

export interface FlowFeedIntake {
  rawVinCount: number
  snapshotVinCount: number
}

/**
 * Phase 2 (additive): classification of the rule-blocked VIN set into how the
 * publish acted on them. Mirrors `ise-srp-back` `FlowBlockedClassification`.
 * Absent on legacy artifacts → readers fall back to the plain blocked count.
 * Counts always sum to the reconciled `blockedCount`.
 */
export interface FlowBlockedClassification {
  newlyBlockedThisRun: number
  alreadyBlockedBeforeRun: number
  unknownBlocked: number
  newlyBlockedVins?: string[]
  alreadyBlockedVins?: string[]
  unknownBlockedVins?: string[]
}

export interface FlowVisibilityListingRules {
  evaluatedCount: number
  acceptedCount: number
  blockedCount: number
  perRuleHits: FlowRuleHit[]
  /** Phase 2 (additive): newly/already/unknown breakdown of blocked VINs. */
  blockedClassification?: FlowBlockedClassification | null
}

export interface FlowSegmentation {
  evaluatedCount: number
  /**
   * Vehicles forwarded out of grouping into the next stage. Grouping never
   * drops vehicles for membership reasons, so this equals `evaluatedCount` on
   * healthy runs. This (not `changedCount`) is the publish-flow handoff count.
   * Optional for read-compat with legacy artifacts written before this field.
   */
  assignedCount?: number
  /**
   * VINs whose group membership changed this run. Drives the grouping outcome
   * copy, NOT the handoff count. Optional for legacy read-compat.
   */
  changedCount?: number
  perSegmentCounts: FlowSegmentCount[]
}

/**
 * Website cache/CDN refresh outcome captured for this publish (mirrors
 * `ise-srp-back/src/flow_metrics/types.ts` `FlowWebsiteRefresh`).
 * Observability-only and neutral across mechanisms.
 */
export type FlowWebsiteRefreshStatus =
  | 'not_captured'
  | 'skipped'
  | 'submitted'
  | 'completed'
  | 'failed'

export type FlowWebsiteRefreshMechanism = 'wordpress_varnish' | 'cloudfront' | 'unknown'

export interface FlowWebsiteRefreshKeysPointer {
  bucket: string
  key: string
  contentType: 'application/x-ndjson'
  recordCount?: number
}

/**
 * Per-type tally of refreshed cache keys (mirrors the backend
 * `FlowWebsiteRefreshKeyTypeCounts`). Derived from the same deduped records as
 * `keyCount`; absent on legacy runs that predate the field.
 */
export interface FlowWebsiteRefreshKeyTypeCounts {
  vehicle?: number
  account?: number
  other?: number
}

export interface FlowWebsiteRefresh {
  status: FlowWebsiteRefreshStatus
  mechanism?: FlowWebsiteRefreshMechanism
  startedAt?: string
  finishedAt?: string
  durationMs?: number
  requestId?: string
  executionArn?: string
  jobId?: string
  /** Count of refreshed cache keys (summary only; list lives in the artifact). */
  keyCount?: number
  /** Per-type breakdown of the refreshed cache keys (summary only). */
  keyTypeCounts?: FlowWebsiteRefreshKeyTypeCounts
  /** True when a refreshed-key artifact was persisted and is API-browsable. */
  recordsAvailable?: boolean
  /** Pointer to the JSONL key artifact (internal/diagnostic; UI uses the API). */
  keysPointer?: FlowWebsiteRefreshKeysPointer
  /** Legacy embedded fields; new runs do not populate these. */
  keys?: string[]
  pathCount?: number
  paths?: string[]
  message?: string
  errorMessage?: string
}

export interface FlowPreProcessorLegacy {
  mode: 'groupSite' | 'sharedInventory' | 'unknown'
  inputCount: number
  outputCount: number
  perSourceCounts?: Array<{
    sourceJuid: string
    sourceFeed: string | null
    vehicleCount: number
    sourceFile?: {
      bucket: string
      key: string
    }
  }>
  outputFile?: {
    bucket: string
    key: string
  }
}

/**
 * Per-feed Feed Preparation source counts for multi-primary publishes.
 *
 * `sourceVinCount` is UNIQUE source VINs within that feed (the backend derives
 * it from `flow-metrics.perFeed[feed].feedIntake.rawVinCount`, which is a
 * VIN-keyed map size — unique VINs, not raw rows). Per-feed values may SUM to
 * more than the top-level count when a VIN appears in multiple feeds, so the
 * sum must never be presented as a deduped unique total.
 */
export interface FlowFeedSourceCount {
  feed: string
  sourceVinCount: number
  eligibleVinCount?: number
  filteredVinCount?: number
  sourceFile?: {
    bucket: string
    key: string
  }
}

export interface FlowPreProcessorSummary {
  status: 'completed' | 'notNeeded' | 'notCaptured' | 'failed'
  reasonCode: string
  explanation: string
  inputVinCount?: number
  outputVinCount?: number
  droppedVinCount?: number
  mode?: string
  evidence?: {
    feedSource?: string
    s3Key?: string
    targetAccountId?: string
    sourceCorrelationId?: string | null
    preProcessorMode?: string | null
  }
  context?: {
    accountUsesSharedInventory?: boolean
    accountBelongsToGroupSite?: boolean
  }
  perSourceCounts?: FlowPreProcessorLegacy['perSourceCounts']
  outputFile?: FlowPreProcessorLegacy['outputFile']
  /** Feeds that contributed source VINs to this publish run (multi-primary). */
  participatingFeeds?: string[]
  /** Per-feed unique source VIN counts (multi-primary). */
  feedSourceCounts?: FlowFeedSourceCount[]
  /** VINs present in more than one feed. Omitted unless cheaply computable. */
  multiFeedVinCount?: number
}

export type FlowPreProcessor = FlowPreProcessorLegacy | FlowPreProcessorSummary

export function isFlowPreProcessorSummary(
  preProcessor: FlowPreProcessor | null | undefined,
): preProcessor is FlowPreProcessorSummary {
  return preProcessor != null && typeof preProcessor === 'object' && 'status' in preProcessor
}

export function isFlowPreProcessorNotNeeded(
  preProcessor: FlowPreProcessor | null | undefined,
): preProcessor is FlowPreProcessorSummary & { status: 'notNeeded' } {
  return isFlowPreProcessorSummary(preProcessor) && preProcessor.status === 'notNeeded'
}

export function getFlowPreProcessorInputCount(
  preProcessor: FlowPreProcessor | null | undefined,
): number | null {
  if (!preProcessor) return null
  if (isFlowPreProcessorSummary(preProcessor)) {
    if (preProcessor.status === 'completed' || preProcessor.status === 'notNeeded') {
      return typeof preProcessor.inputVinCount === 'number' ? preProcessor.inputVinCount : null
    }
    return null
  }
  return preProcessor.inputCount
}

export function getFlowPreProcessorOutputCount(
  preProcessor: FlowPreProcessor | null | undefined,
): number | null {
  if (!preProcessor) return null
  if (isFlowPreProcessorSummary(preProcessor)) {
    if (preProcessor.status === 'completed' || preProcessor.status === 'notNeeded') {
      return typeof preProcessor.outputVinCount === 'number' ? preProcessor.outputVinCount : null
    }
    return null
  }
  return preProcessor.outputCount
}

export function getFlowPreProcessorDroppedCount(
  preProcessor: FlowPreProcessor | null | undefined,
): number {
  if (!preProcessor) return 0
  if (isFlowPreProcessorSummary(preProcessor)) {
    if (preProcessor.status === 'completed' || preProcessor.status === 'notNeeded') {
      if (typeof preProcessor.droppedVinCount === 'number') {
        return Math.max(0, preProcessor.droppedVinCount)
      }
      const inputCount = getFlowPreProcessorInputCount(preProcessor)
      const outputCount = getFlowPreProcessorOutputCount(preProcessor)
      if (inputCount == null || outputCount == null) return 0
      return Math.max(0, inputCount - outputCount)
    }
    return 0
  }
  return Math.max(0, preProcessor.inputCount - preProcessor.outputCount)
}

/**
 * Per-feed source counts attached to a multi-primary Feed Preparation summary.
 * Returns `[]` for single-feed or legacy artifacts (graceful fallback).
 */
export function getFlowPreProcessorFeedSourceCounts(
  preProcessor: FlowPreProcessor | null | undefined,
): FlowFeedSourceCount[] {
  if (!isFlowPreProcessorSummary(preProcessor)) return []
  const counts = preProcessor.feedSourceCounts
  if (!Array.isArray(counts)) return []
  return counts
    .filter((c) => c && typeof c.feed === 'string' && c.feed.trim().length > 0)
    .map((c) => {
      const bucket = String(c.sourceFile?.bucket ?? '').trim()
      const key = String(c.sourceFile?.key ?? '').trim()
      return {
        feed: c.feed,
        sourceVinCount: Math.max(0, Number(c.sourceVinCount) || 0),
        eligibleVinCount:
          typeof c.eligibleVinCount === 'number' ? Math.max(0, c.eligibleVinCount) : undefined,
        filteredVinCount:
          typeof c.filteredVinCount === 'number' ? Math.max(0, c.filteredVinCount) : undefined,
        ...(bucket && key ? { sourceFile: { bucket, key } } : {}),
      }
    })
}

/**
 * Participating feeds for this run. Prefers the explicit `participatingFeeds`
 * field, falling back to feed keys present in `feedSourceCounts`.
 */
export function getFlowPreProcessorParticipatingFeeds(
  preProcessor: FlowPreProcessor | null | undefined,
): string[] {
  if (!isFlowPreProcessorSummary(preProcessor)) return []
  const explicit = preProcessor.participatingFeeds
  if (Array.isArray(explicit) && explicit.length > 0) {
    return explicit.filter((f): f is string => typeof f === 'string' && f.trim().length > 0)
  }
  return getFlowPreProcessorFeedSourceCounts(preProcessor).map((c) => c.feed)
}

/** `true` when more than one feed contributed source VINs to this run. */
export function isMultiFeedPreProcessor(
  preProcessor: FlowPreProcessor | null | undefined,
): boolean {
  return getFlowPreProcessorParticipatingFeeds(preProcessor).length > 1
}

export interface FlowMetricsPerFeed {
  feedSource: string
  updatedAt: string
  feedIntake: FlowFeedIntake | null
}

export interface FlowMetricsPublishWide {
  updatedAt: string
  preProcessor: FlowPreProcessor | null
  visibilityListingRules: FlowVisibilityListingRules | null
  segmentation: FlowSegmentation | null
  websiteRefresh?: FlowWebsiteRefresh | null
}

export type FlowStageName =
  | 'preProcessor'
  | 'feedIntake'
  | 'visibilityListingRules'
  | 'segmentation'

export interface FlowMetricsArtifact {
  version: string
  jazelId: string
  correlationId: string
  generatedAt: string
  lastUpdatedAt: string
  perFeed: Record<string, FlowMetricsPerFeed>
  publishWide: FlowMetricsPublishWide
  missing: FlowStageName[]
}

export type FlowMetricsMissingReason = 'NOT_INSTRUMENTED'

export interface FlowMetricsResponse {
  jazelId: string
  correlationId: string
  metrics: FlowMetricsArtifact | null
  missingReason?: FlowMetricsMissingReason
  details?: string
}

function metricsPath(jazelId: string, correlationId: string): string {
  return `/publish-flow-metrics/${encodeURIComponent(jazelId)}/${encodeURIComponent(correlationId)}`
}

async function readResponse(res: Response): Promise<FlowMetricsResponse> {
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`)
    throw new Error('Invalid response from publish flow metrics API.')
  }

  const typed = body as Partial<FlowMetricsResponse> & { error?: string }

  if (res.ok) return typed as FlowMetricsResponse
  if (res.status === 404 && typed?.missingReason) return typed as FlowMetricsResponse

  throw new Error(typed?.error || typed?.details || `HTTP ${res.status} ${res.statusText}`)
}

export async function getPublishFlowMetrics(
  endpoint: string,
  jazelId: string,
  correlationId: string,
): Promise<FlowMetricsResponse> {
  const url = `${endpoint}${metricsPath(jazelId, correlationId)}`
  const res = await fetch(url, { method: 'GET' })
  return readResponse(res)
}

/* -------------------------------------------------------------------------- */
/* Pure view-model helpers                                                    */
/* -------------------------------------------------------------------------- */

/**
 * `true` when the artifact has at least one measured stage (anything other
 * than every-stage-missing). UIs use this to avoid rendering a noisy "we
 * measured nothing" card.
 */
export function hasAnyMeasuredStage(metrics: FlowMetricsArtifact | null | undefined): boolean {
  if (!metrics) return false
  if (metrics.publishWide.preProcessor) return true
  if (metrics.publishWide.visibilityListingRules) return true
  if (metrics.publishWide.segmentation) return true
  for (const slice of Object.values(metrics.perFeed)) {
    if (slice.feedIntake) return true
  }
  return false
}

export interface FeedIntakeRowViewModel {
  feedSource: string
  rawVinCount: number
  snapshotVinCount: number
  /** Difference rawVinCount - snapshotVinCount (often 0; positive means dedupe/parse drop). */
  droppedDuringIntake: number
}

export function buildFeedIntakeRows(
  metrics: FlowMetricsArtifact | null | undefined,
): FeedIntakeRowViewModel[] {
  if (!metrics) return []
  return Object.values(metrics.perFeed)
    .filter((slice) => slice.feedIntake)
    .map((slice) => {
      const raw = slice.feedIntake?.rawVinCount ?? 0
      const snap = slice.feedIntake?.snapshotVinCount ?? 0
      return {
        feedSource: slice.feedSource,
        rawVinCount: raw,
        snapshotVinCount: snap,
        droppedDuringIntake: Math.max(0, raw - snap),
      }
    })
    .sort(
      (a, b) => b.snapshotVinCount - a.snapshotVinCount || a.feedSource.localeCompare(b.feedSource),
    )
}

export interface VisibilityRuleRowViewModel {
  ruleId: string
  label: string
  matchedCount: number
  /** Share of total blocked VINs attributed to this rule (0..1). */
  share: number
}

export function buildVisibilityRuleRows(
  metrics: FlowMetricsArtifact | null | undefined,
): VisibilityRuleRowViewModel[] {
  const vis = metrics?.publishWide.visibilityListingRules
  if (!vis || vis.perRuleHits.length === 0) return []
  const total = vis.blockedCount || vis.perRuleHits.reduce((s, h) => s + h.matchedCount, 0)
  return vis.perRuleHits.map((h) => ({
    ruleId: h.ruleId,
    label: formatVisibilityRuleLabel(h.label || h.ruleId),
    matchedCount: h.matchedCount,
    share: total > 0 ? h.matchedCount / total : 0,
  }))
}

/**
 * Normalize a generated Feed Source Config source-authority rule label into
 * dealer-friendly copy. The publish writer emits these as
 * `[auto:feed-mapping] New + Ready for Sale must come from fdds2`; we strip the
 * generator prefix and rewrite "must come from <feed>" as
 * "must also be present in <FEED>" so the copy reflects that source authority
 * checks known feed membership across feeds (not only the triggering feed).
 *
 * Non-generated labels pass through unchanged (prefix stripped). Shared by the
 * Publish Rules Evaluation card and the Website Visibility per-VIN detail.
 */
export function formatVisibilityRuleLabel(label: string): string {
  const normalized = label.trim()
  const generatedFeedMappingLabel = normalized.replace(/^\[auto:feed-mapping]\s*/i, '')
  const match = generatedFeedMappingLabel.match(/^(.+?) must come from ([A-Za-z0-9_-]+)$/i)

  if (!match) return normalized

  return `${match[1]} must also be present in ${formatFeedCodeForAuthorityCopy(match[2] ?? '')}`
}

/**
 * True when a rule label is a generated Feed Source Config source-authority
 * rule (`[auto:feed-mapping] ... must come from <feed>`). Drives the optional
 * "source authority checks known feed membership across feeds" helper.
 */
export function isGeneratedSourceAuthorityRuleLabel(label: string): boolean {
  const normalized = String(label ?? '').trim()
  if (!/^\[auto:feed-mapping]/i.test(normalized)) return false
  return / must come from [A-Za-z0-9_-]+$/i.test(normalized.replace(/^\[auto:feed-mapping]\s*/i, ''))
}

const PUBLISH_ELIGIBILITY_GENERIC_RULE_LABEL = 'Matched publish rule'
const PUBLISH_ELIGIBILITY_GENERIC_REASON =
  'Vehicle matched a blocking publish rule.'

function looksLikeInternalRuleIdentifier(value: string): boolean {
  const trimmed = value.trim()
  if (!trimmed) return false
  return /^[A-Z0-9][A-Z0-9_:.-]*$/.test(trimmed) && /[_:.]/.test(trimmed)
}

function isSyntheticBlockedByPublishRulesLabel(ruleId: string, label?: string | null): boolean {
  const id = String(ruleId ?? '').trim()
  const normalizedId = id.toLowerCase()
  const labelTrim = String(label ?? '').trim()
  return (
    normalizedId === 'blocked_by_publish_rules' ||
    /^controller:blocked/i.test(id) ||
    labelTrim === 'Blocked by Publish Rules'
  )
}

/** True when a rule id/label is generated or internal — not a dealer-configured name. */
export function isInternalOrGeneratedPublishRuleLabel(
  ruleId: string,
  label?: string | null,
): boolean {
  const id = String(ruleId ?? '').trim()
  const labelTrim = String(label ?? '').trim()

  if (id === 'RULE_MATCH' || labelTrim === 'RULE_MATCH') return true
  if (isSyntheticBlockedByPublishRulesLabel(id, labelTrim)) return true

  if (labelTrim && isGeneratedSourceAuthorityRuleLabel(labelTrim)) return false
  if (isGeneratedSourceAuthorityRuleLabel(id)) return false

  if (labelTrim && !looksLikeInternalRuleIdentifier(labelTrim)) return false

  const displayLabel = formatVisibilityRuleLabel(labelTrim || id)
  if (displayLabel && displayLabel !== id && !looksLikeInternalRuleIdentifier(displayLabel)) {
    return false
  }

  return true
}

export function isHumanFacingPublishRuleLabel(ruleId: string, label?: string | null): boolean {
  return !isInternalOrGeneratedPublishRuleLabel(ruleId, label)
}

/** Dialog rule label: human names preserved; internal/generated ids use a friendly fallback. */
export function formatPublishEligibilityDialogRuleLabel(
  ruleId: string,
  label?: string | null,
): string {
  if (isHumanFacingPublishRuleLabel(ruleId, label)) {
    return formatVisibilityRuleLabel(String(label ?? '').trim() || ruleId)
  }
  return PUBLISH_ELIGIBILITY_GENERIC_RULE_LABEL
}

/** Dialog reason copy: strip internal rule ids and synthetic blocked-by-publish-rules labels. */
export function formatPublishEligibilityReasonExplanation(
  explanation: string,
  ruleId?: string | null,
): string {
  const trimmed = String(explanation ?? '').trim()
  if (!trimmed) return trimmed

  if (/Vehicle moved from accepted to blocked\s*\(RULE_MATCH\)/i.test(trimmed)) {
    return PUBLISH_ELIGIBILITY_GENERIC_REASON
  }

  const parensMatch = trimmed.match(/^Vehicle moved from accepted to blocked\s*\(([^)]+)\)\.?$/i)
  if (parensMatch) {
    const inner = parensMatch[1].trim()
    if (
      inner === 'RULE_MATCH' ||
      isSyntheticBlockedByPublishRulesLabel(inner, inner) ||
      isInternalOrGeneratedPublishRuleLabel(inner, inner)
    ) {
      return PUBLISH_ELIGIBILITY_GENERIC_REASON
    }
    return `${PUBLISH_ELIGIBILITY_GENERIC_REASON.slice(0, -1)} (${formatPublishEligibilityDialogRuleLabel(inner)}).`
  }

  if (/^Vehicle moved from accepted to blocked\.?$/i.test(trimmed)) {
    return PUBLISH_ELIGIBILITY_GENERIC_REASON
  }

  if (ruleId && trimmed.includes('RULE_MATCH')) {
    return PUBLISH_ELIGIBILITY_GENERIC_REASON
  }

  return trimmed
}

/** Fallback "Why it matched" copy when no specific reason metadata was captured. */
export function publishEligibilityFallbackReason(): string {
  return PUBLISH_ELIGIBILITY_GENERIC_REASON
}

/** Captured reason fields for a single blocked VIN, used to explain why it matched. */
export interface PublishEligibilityReasonInput {
  ruleId?: string | null
  field?: string | null
  operator?: string | null
  expectedValue?: unknown
  actualValue?: unknown
  afterValue?: unknown
  sourceAfter?: string | null
  explanation?: string | null
  isSourceAuthority?: boolean
  requiredFeed?: string | null
}

const PUBLISH_RULE_OPERATOR_COPY: Record<string, string> = {
  equals: 'equals',
  eq: 'equals',
  '=': 'equals',
  '==': 'equals',
  not_equals: 'does not equal',
  ne: 'does not equal',
  '!=': 'does not equal',
  contains: 'contains',
  not_contains: 'does not contain',
  in: 'is one of',
  not_in: 'is not one of',
  greater_than: 'is greater than',
  gt: 'is greater than',
  less_than: 'is less than',
  lt: 'is less than',
  greater_than_or_equal: 'is at least',
  gte: 'is at least',
  less_than_or_equal: 'is at most',
  lte: 'is at most',
  starts_with: 'starts with',
  ends_with: 'ends with',
}

const PUBLISH_RULE_EMPTINESS_OPERATORS = new Set([
  'is_empty',
  'empty',
  'is_blank',
  'blank',
  'missing',
  'is_missing',
  'not_set',
  'is_null',
  'null',
])

const PUBLISH_RULE_PRESENCE_OPERATORS = new Set([
  'is_not_empty',
  'not_empty',
  'exists',
  'is_present',
  'present',
  'is_set',
])

function formatPublishRuleValue(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (Array.isArray(value)) {
    return value
      .map((entry) => formatPublishRuleValue(entry))
      .filter(Boolean)
      .join(', ')
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value)
    } catch {
      return ''
    }
  }
  return String(value).trim()
}

/**
 * Build dealer-readable "Why it matched" bullet lines from a blocked VIN's
 * captured reason metadata. Structured criterion fields (field/operator/
 * expected/actual) are surfaced even when the rule label itself is internal —
 * we only hide raw internal identifiers, never the captured detail. Returns an
 * empty array when no specific reason was captured (caller shows the fallback).
 */
export function buildPublishEligibilityWhyMatchedLines(
  input: PublishEligibilityReasonInput,
): string[] {
  const lines: string[] = []

  const field = String(input.field ?? '').trim()
  const operatorRaw = String(input.operator ?? '')
    .trim()
    .toLowerCase()
  const expected = input.expectedValue
  const actual = input.actualValue !== undefined ? input.actualValue : input.afterValue

  if (field) {
    if (PUBLISH_RULE_EMPTINESS_OPERATORS.has(operatorRaw)) {
      lines.push(`${field} is missing`)
    } else if (PUBLISH_RULE_PRESENCE_OPERATORS.has(operatorRaw)) {
      lines.push(`${field} is present`)
    } else {
      const operatorCopy =
        PUBLISH_RULE_OPERATOR_COPY[operatorRaw] ??
        (operatorRaw ? operatorRaw.replace(/_/g, ' ') : '')
      const expectedText = formatPublishRuleValue(expected)
      if (operatorCopy && expectedText) {
        lines.push(`Condition: ${field} ${operatorCopy} ${expectedText}`)
      } else if (expectedText) {
        lines.push(`Condition: ${field} equals ${expectedText}`)
      } else {
        lines.push(`Condition: ${field}`)
      }
      const actualText = formatPublishRuleValue(actual)
      if (actualText) lines.push(`Actual: ${actualText}`)
    }
  }

  const sourceAfter = String(input.sourceAfter ?? '').trim()
  if (sourceAfter && !field) {
    lines.push(`Source: ${sourceAfter}`)
  }

  if (input.isSourceAuthority) {
    const requiredFeed = String(input.requiredFeed ?? '').trim()
    if (requiredFeed) {
      lines.push(`Available feeds do not include ${requiredFeed}.`)
    }
  }

  const explanation = String(input.explanation ?? '').trim()
  const explanationIsGenericTemplate =
    /^vehicle moved from accepted to blocked/i.test(explanation) ||
    /^vehicle remains/i.test(explanation)
  if (
    explanation &&
    !explanationIsGenericTemplate &&
    !explanation.includes('RULE_MATCH') &&
    !isInternalOrGeneratedPublishRuleLabel(String(input.ruleId ?? ''), explanation) &&
    !lines.includes(explanation)
  ) {
    lines.push(explanation)
  }

  return lines
}

/**
 * Expanded Publish Eligibility rule breakdown. Internal/generated rule hits are
 * collapsed into a non-ambiguous "Other blocking rule matches" line; human rule
 * names stay separate.
 */
export function buildPublishEligibilityExpandedRuleMatchLines(
  rows: VisibilityRuleRowViewModel[],
  totalBlocked?: number,
): string[] | undefined {
  if (rows.length === 0) return undefined

  const humanRows = rows.filter((row) => isHumanFacingPublishRuleLabel(row.ruleId, row.label))
  const internalCount = rows
    .filter((row) => isInternalOrGeneratedPublishRuleLabel(row.ruleId, row.label))
    .reduce((sum, row) => sum + Math.max(0, Math.floor(row.matchedCount)), 0)

  const lines: string[] = []

  if (internalCount > 0) {
    const matchWord = internalCount === 1 ? 'match' : 'matches'
    lines.push(`${internalCount} Other blocking rule ${matchWord}`)
  }

  for (const row of humanRows) {
    const count = Math.max(0, Math.floor(row.matchedCount))
    const label = formatVisibilityRuleLabel(row.label || row.ruleId)
    lines.push(`${count} ${label}`)
  }

  if (lines.length === 0 && typeof totalBlocked === 'number' && totalBlocked > 0) {
    const blocked = Math.max(0, Math.floor(totalBlocked))
    const matchWord = blocked === 1 ? 'match' : 'matches'
    lines.push(`${blocked} Other blocking rule ${matchWord}`)
  }

  return lines.length > 0 ? lines : undefined
}

function formatFeedCodeForAuthorityCopy(feed: string): string {
  const normalized = feed.trim()
  if (/^fdds\d+$/i.test(normalized)) return normalized.toUpperCase()
  if (normalized.toLowerCase() === 'vauto') return 'vAuto'
  return normalized
}

export interface SegmentRowViewModel {
  segmentId: string
  label: string
  vehicleCount: number
  /** Share of evaluated VINs that match this segment (0..1). Segments may overlap. */
  share: number
}

export function buildSegmentRows(
  metrics: FlowMetricsArtifact | null | undefined,
): SegmentRowViewModel[] {
  const seg = metrics?.publishWide.segmentation
  if (!seg || seg.perSegmentCounts.length === 0) return []
  const total = seg.evaluatedCount
  return seg.perSegmentCounts.map((s) => ({
    segmentId: s.segmentId,
    label: s.label || s.segmentId,
    vehicleCount: s.vehicleCount,
    share: total > 0 ? s.vehicleCount / total : 0,
  }))
}
